import { styled } from '@linaria/react'
import React, {
  type ComponentProps,
  type FC,
  forwardRef,
  type ReactNode,
} from 'react'
import {
  type FontSize,
  type FontWeight,
  fontWeightMapCss,
  Typography,
  typographyMapCss,
} from './Typography'
import { css, cx } from '@linaria/core'
import { colors } from '../styles/colors'

export const InputText: FC<InputBaseProps> = props => {
  return <Input type="text" {...props} />
}
export const InputNumber: FC<InputBaseProps> = props => {
  return <Input type="number" {...props} />
}
export const InputFile: FC<InputBaseProps> = props => {
  return <Input type="file" {...props} />
}
export const InputDateTime: FC<InputBaseProps> = props => {
  return <Input type="datetime-local" {...props} className={dateTimeCss} />
}

interface CommonProps {
  fontSize?: FontSize
  fontWeight?: FontWeight
  label?: ReactNode
}
type InputBaseProps = ComponentProps<typeof StyledInput> & CommonProps

export const Input = forwardRef<
  HTMLInputElement,
  ComponentProps<typeof StyledInput> & CommonProps
>(({ fontSize = 14, fontWeight = 400, label, ...restProps }, ref) => {
  return (
    <Label fontSize={fontSize} fontWeight={fontWeight}>
      {label}
      <StyledInput
        {...restProps}
        ref={ref}
        className={cx(
          restProps.className,
          typographyMapCss[fontSize],
          fontWeightMapCss[fontWeight],
        )}
      />
    </Label>
  )
})

Input.displayName = 'Input'

export const InputTextarea = forwardRef<
  HTMLTextAreaElement,
  ComponentProps<typeof StyledTextArea> & CommonProps
>(({ fontSize = 14, fontWeight = 400, label, ...restProps }, ref) => {
  return (
    <Label fontSize={fontSize} fontWeight={fontWeight}>
      {label}
      <StyledTextArea
        {...restProps}
        ref={ref}
        className={cx(typographyMapCss[fontSize], fontWeightMapCss[fontWeight])}
      />
    </Label>
  )
})

InputTextarea.displayName = 'TextAreaBase'

const commonCss = `
  height: 42px;
  width: 100%;
  display: block;
  padding: 12px 16px;
  border-radius: 16px;
  border: 1px solid ${colors.borderPrimary};
  background-color: ${colors.inputBg};
  outline: none;
  ::placeholder {
    color: ${colors.inputPlaceholder};
  }
  color: ${colors.inputText};
  box-sizing: border-box;

  &:focus {
    border-color: ${colors.inputBorderFocus};
  }
`
const dateTimeCss = css`
  -webkit-appearance: none;
  &::-webkit-date-and-time-value {
    text-align: left;
  }
`

const StyledInput = styled.input`
  ${commonCss};
`
const StyledTextArea = styled.textarea`
  ${commonCss};
  resize: none;
`

const Label = styled(Typography)`
  display: grid;
  gap: 4px;
`
